import {AccelKeyMgr, Action, EButtonUiContext} from "/@lib@/commons/actions.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {SEC} from "/@lib@/commons/security.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IO} from "/@lib@/commons/io/io.js";
import {JRemoteResDescFromClient, REMOTE} from "/@lib@/core/remote.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IWspUiEnv, JWspDefProps, Wsp, WSP} from "/@lib@/wsp/wsp.js";
import {EItemTypeFamily, ItemType} from "/@lib@/wsp/wspMetaUi.js";
import {
	FolderEntries,
	IImportFromUrlSvc,
	IImportInterWspSvc,
	IImportInWspSvc,
	InfoFocusItem,
	InfoFocusNodeInItem,
	InfoRefreshItemDisplays,
	InfoSelectUris,
	IShortDescCtx,
	IShortDescsTransfer,
	IShortDescUrlTransfer,
	ITEM,
	OImportInterWspOptions,
	OImportRepeatStates,
	OImportSrcOptions
} from "/@lib@/wsp/item.js";
import {ESrcSt, JSrcFields, JSrcFieldsTree, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";

import {POPUP, PopupConfirm} from "/@back@/commons/widgets/popups.js";
import {IFolder, isFolder} from "/@lib@/commons/io/files.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {Workbench} from "/@back@/core/widgets/workbench.js";
import {SrcNewCode} from "/@back@/wsp/dialogs/srcNewCode.js";
import {ExportWspAction, IWspActionCtx} from "/@back@/wsp/actions/wspActions.js";

import type {NetItems} from "/@back@/wsp/views/netItems.js";
import type {IGridDataRow} from "/@back@/commons/widgets/grid-core.js";
import type {SpaceSelector} from "/@back@/wsp/dialogs/spaceSelector.js";
import type {OWspTrashInit} from "/@back@/wsp/dialogs/wspTrash.js";
import type {OObjRolesMapInit} from "/@back@/core/dialogs/objRolesMap.js";
import type {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import type {OSearchItemsInit, SearchItems} from "/@back@/wsp/dialogs/searchItems.js";
import type {ISrcGridCtx} from "/@back@/wsp/widgets/srcGrid.js";
import type {ISearchStore} from "/@lib@/wsp/search.js";
import type {IInfoBroker, IInfoBrokerPointer, IInfoProducer} from "/@lib@/commons/infos.js";
import type {OAppHeaderInit} from "/@back@/core/widgets/appHeader.js";
import type {TplRequestsMenu} from "/@back@/wsp/actions/searchesActions.js";
import type {TaskCritForMe} from "/@back@/wsp/widgets/search/taskCrit.js";
import type {ItemCritTitleRegExp} from "/@back@/wsp/widgets/search/itemCrit.js";
import type {UiCritBool} from "/@back@/wsp/widgets/search/uiCrit.js";
import type {IWedSearchCoordinatorPointer} from "/@back@/edit/wed/features/searchBar.js";
import type {JItemWedSearchBarParams} from "/@back@/wsp/widgets/wed/wspSearchBar.js";
import {LASTDATAS} from "/@lib@/commons/lastDatas.js";
import {IWedDiffCoordinatorPointer, JWedDiffBarParams, WedDiffCoordinator} from "/@back@/edit/wed/features/diffBar.js";

/**
 *
 */
export class SrcAction<C extends IShortDescCtx> extends Action<C> {

	static getInfoBroker(ctx: IShortDescCtx | IInfoBrokerPointer | IWspActionCtx | IRegPointer<IWspUiEnv>): IInfoBroker {return (ctx as IInfoBrokerPointer).infoBroker || (ctx as IRegPointer<any>).reg?.env.infoBroker}

	atLeastOne: boolean;
	exactOne: boolean;
	zeroOrOne: boolean;
	/** liste blanche de certaines familles d'items/res. */
	families: EItemTypeFamily[];
	/** limite aux items (espaces exclus) avec liste noire de certaines familles d'items/res. */
	noFamilies: EItemTypeFamily[];
	isBackendDb: boolean;
	isNotRemoved: boolean = true; //par défaut action désactivée sur une entrée est en état supprimée

	/* Perms appliquées sur chaque src (avec ses srcRoles et srcRi) pour évaluer la visibilité de l'action. */
	protected _visSrcPerms: string[];

	/* Perms appliquées sur chaque src (avec ses srcRoles et srcRi) pour évaluer l'activation de l'action. */
	protected _enableSrcPerms: string[];

	isVisible(ctx: C): boolean {
		if (this.isBackendDb && ctx.reg.env.wsp.backEnd === "fs") return false;
		if (this.atLeastOne && ctx.shortDescs.length < 1) return false;
		if (this.exactOne && ctx.shortDescs.length !== 1) return false;
		if (this.zeroOrOne && ctx.shortDescs.length > 1) return false;
		if (this.families || this.noFamilies) {
			const wspMetaUi = ctx.reg.env.wsp.wspMetaUi;
			for (const shortDesc of ctx.shortDescs) {
				if (ITEM.getSrcUriType(shortDesc.srcUri) === "space") return false;
				if (this.families && !wspMetaUi.getItemType(shortDesc.itModel).isFamily(...this.families)) return false;
				if (this.noFamilies && wspMetaUi.getItemType(shortDesc.itModel).isFamily(...this.noFamilies)) return false;
			}
		}
		if (!super.isVisible(ctx)) return false;
		return this.checkSrcPermsForAll(this._visSrcPerms, ctx);
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		if (this.isNotRemoved && ctx.shortDescs.findIndex((src) => src.srcSt < 0) >= 0) return false;
		if ((this as IMoveAction).isMove) {
			//On est une action de déplacement d'un noeud.
			const wspDefProps = ctx.reg.env.wsp.wspDefProps;
			if (ctx.shortDescs.some((sd) => {
				//Pas de move sur une uri externe
				if (ITEM.isExtItem(sd.srcUri)) return true;
				if (this.isSrcLayered(wspDefProps, sd)) {
					if (sd.drvState) return true; //drv: jamais déplacable.
					//drf: check perm spéciale pour changer l'arbo de la ref.
					if (!ctx.reg.hasPermission("action.src#update.outsideLayer", sd.srcRoles, null, sd.srcRi)) return true;
				}
				return false;
			})) return false;

		}
		return this.checkSrcPermsForAll(this._enableSrcPerms, ctx)
	}

	/** Ce noeud est-il une surcouche d'un layer inférieur ? */
	protected isSrcLayered(wspDefProps: JWspDefProps, sd: JSrcFields): boolean {
		if (wspDefProps.drfRefWsp) {
			return sd.drfState !== "created" && sd.drfState !== "createdDeleted";
		} else if (wspDefProps.drvAxis) {
			if (sd.drvState.startsWith("created")) return false; //contenu propre à l'atelier
			if (sd.drvState === "erased" && sd.drvAxisDefCo === null) return false; //contenu supprimé mais aucun axis dans la chaine défini ce contenu et master phantom => propre à l'atelier
			return true;
		}
		return false;
	}

	getFirstSrcUri(ctx: IShortDescCtx): srcUri {return ctx.shortDescs.length >= 1 ? ctx.shortDescs[0].srcUri : null}

	checkSrcPermsForAll(perms: string | string[], ctx: IShortDescCtx): boolean {
		if (perms) {
			const reg = REG.getReg(ctx);
			if (ctx.shortDescs.length === 0) {
				//Aucun src listé => perm sur l'atelier
				if (!reg.hasPerm(perms)) return false;
			} else {
				for (const sd of ctx.shortDescs) if (!reg.hasPermission(perms, sd.srcRoles, null, sd.srcRi)) return false;
			}
		}
		return true
	}

	requireSrcVisiblePerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireSrcVisiblePerm(perms[i]);
			} else {
				if (!this._visSrcPerms) this._visSrcPerms = [];
				if (this._visSrcPerms.indexOf(perms) == -1) this._visSrcPerms.push(perms);
			}
		}
		return this;
	}

	/**
	 * @param perms logique "or" (cf {@link IReg.hasPermission})
	 */
	requireSrcEnabledPerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireSrcEnabledPerm(perms[i]);
			} else {
				if (!this._enableSrcPerms) this._enableSrcPerms = [];
				if (this._enableSrcPerms.indexOf(perms) == -1) this._enableSrcPerms.push(perms);
			}
		}
		return this;
	}
}

export class ShortDescCopy extends SrcAction<IShortDescCtx> {
	constructor(id?: string) {
		super(id || 'copy');
		this._label = "Copier";
		this._group = "clipboard";
		this._icon = "/@skin@/wsp/actions/copyItem.svg";
		this.atLeastOne = true;
	}

	execute(ctx: IShortDescCtx, ev?: Event): Promise<void> {
		return ITEM.setShortDescTransferToClipboard(ctx);
	}

	static SINGLETON = new ShortDescCopy();
}

/** Action qui affiche et focus un seul item. */
export class FocusItem extends SrcAction<IShortDescCtx> {

	static gotoItem(infoBroker: IInfoBroker, sd: JSrcFields, from: IInfoProducer) {
		if (infoBroker && sd) {
			if (sd.itSubItem) {
				infoBroker.dispatchInfo(new InfoFocusNodeInItem(`//*[@xml:id='${sd.itSubItem.id}']`, SRC.srcRef(sd), sd), from);
			} else {
				infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(sd), sd), from);
			}
		}
	}

	constructor(id?: string) {
		super(id || 'focusItem');
		this._label = "Afficher cet item";
		this._icon = "/@skin@/wsp/actions/showItem.svg";
		this._group = "open";
		this.exactOne = true;
		this.isNotRemoved = false;
	}

	isVisible(ctx: IShortDescCtx): boolean {
		const infoBroker = SrcAction.getInfoBroker(ctx);
		if (!infoBroker) return false;
		if (ITEM.getSrcUriType(this.getFirstSrcUri(ctx) || '') === "space") return false;
		return super.isVisible(ctx);
	}

	protected isVisibleSuper(ctx: IShortDescCtx) {
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IShortDescCtx) {
		if (ITEM.getSrcUriType(this.getFirstSrcUri(ctx) || '') !== "item") return false;
		return super.isEnabled(ctx);
	}

	protected isEnabledSuper(ctx: IShortDescCtx) {
		return super.isEnabled(ctx);
	}

	execute(ctx: IShortDescCtx, ev?: Event) {
		const infoBroker = SrcAction.getInfoBroker(ctx);
		FocusItem.gotoItem(infoBroker, ctx.shortDescs[0], ctx.emitter);
	}
}

/**
 * Focus un item et y recherche une référence d'item donnée
 */
export class FocusItemSelRef extends FocusItem {

	static SINGLETON = new FocusItemSelRef();

	execute(ctx: IShortDescCtx, ev?: Event) {
		(ctx.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord?.setParamsOnce({
			searchId: 'item',
			findRef: SRC.srcRef((ctx.reg.env as IItemUiEnv).longDesc)
		} as JItemWedSearchBarParams);
		super.execute(ctx, ev);
	}
}

/**
 * Focus un item et y exécute un Diff donné (le premier dispo par défaut)
 */
export class FocusItemDiff extends FocusItem {

	protected diffBarParams?: JWedDiffBarParams | ((ctx: IShortDescCtx) => JWedDiffBarParams | null);

	setDiffBarParams(params: JWedDiffBarParams | ((ctx: IShortDescCtx) => JWedDiffBarParams)): this {
		this.diffBarParams = params;
		return this;
	}

	execute(ctx: IShortDescCtx, ev?: Event) {
		const diffBarParams: JWedDiffBarParams = typeof this.diffBarParams === 'function' ? this.diffBarParams(ctx) : this.diffBarParams;
		if (diffBarParams) {
			const env = ctx.reg.env as IWedDiffCoordinatorPointer;
			if (!env.wedDiffCoord) env.wedDiffCoord = new WedDiffCoordinator();
			env.wedDiffCoord.setParamsOnce(diffBarParams);
		}
		super.execute(ctx, ev);
	}
}

export class FocusItemOrSpace extends FocusItem {

	getLabel(ctx: IShortDescCtx): string {
		return ITEM.getSrcUriType(this.getFirstSrcUri(ctx) || '') === "space" ? "Explorer cet espace" : "Afficher cet item";
	}

	isVisible(ctx: IShortDescCtx): boolean {
		const infoBroker = SrcAction.getInfoBroker(ctx);
		if (!infoBroker) return false;
		return super.isVisibleSuper(ctx);
	}

	isEnabled(ctx: IShortDescCtx) {
		//res assimilé à un item ?
		if (!this.getFirstSrcUri(ctx)) return false;
		return super.isEnabledSuper(ctx);
	}
}

/** Mémoire des dernières recherches fastFind enregsitrées dans les lastDatas. */
let fastFindGlobalLastTerms: string[];

/**
 *
 */
export class OpenFastFindItem extends Action<IRegPointer<IWspUiEnv>> {

	static async fastFind(reg: IReg<IWspUiEnv>, ev?: Event): Promise<JSrcFields | null> {
		//Chargement asynch de FastFindItem
		const target = ev?.target as HTMLElement; //avant async
		const {FastFindItem} = await import("/@back@/wsp/dialogs/fastFindItem.js");
		const {ItemCreator} = await import("/@back@/wsp/dialogs/itemCreator.js");
		if (!fastFindGlobalLastTerms) {
			fastFindGlobalLastTerms = LASTDATAS.getLastDatas(document.body, "fastFindGlobalLastTerms") || [];
			LASTDATAS.buildLastDatasHooks.add((ld) => {
				if (fastFindGlobalLastTerms.length > 10) fastFindGlobalLastTerms.splice(0, fastFindGlobalLastTerms.length - 10);
				ld["fastFindGlobalLastTerms"] = fastFindGlobalLastTerms;
			});
		}
		if (fastFindGlobalLastTerms.length > 40) fastFindGlobalLastTerms.splice(0, 10);
		return await FastFindItem.openFastFind({
			reg,
			previousTerms: fastFindGlobalLastTerms,
			itemCreator: reg.getPref("wsp.createItem", true) ? ItemCreator.setDefaultConfigFromReg(reg) : undefined
		}, target || reg.env.uiRoot, ev).onNextClose();
	}

	constructor() {
		super("fastFindItem");
		this._label = "Recherche rapide";
		this._icon = "/@skin@/wsp/actions/fastFind.svg";
		this._group = 'search';
	}

	async execute(ctx: IRegPointer<IWspUiEnv>, ev?: Event) {
		this.doWithResult(ctx, await OpenFastFindItem.fastFind(ctx.reg, ev));
	}

	doWithResult(ctx: IRegPointer<IWspUiEnv>, sd: JSrcFields) {
		const infoBroker = SrcAction.getInfoBroker(ctx);
		if (sd && infoBroker) {
			infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(sd), sd), ctx.reg.env.uiRoot);
		}
	}
}

/**
 * Ouverture de l'écran de recherche d'item, ou de taches
 */
export class OpenSearchItems extends Action<IRegPointer<IWspUiEnv> | IShortDescCtx> {

	constructor(protected kind: 'item' | 'task' = 'item') {
		super("searchItems");
		this._label = kind === 'item' ? "Recherche d'items..." : "Recherche de tâches...";
		this._icon = "/@skin@/wsp/actions/search.svg";
		this._group = 'tools';
	}

	async execute(ctx: IRegPointer<IWspUiEnv> | IShortDescCtx, ev?: Event) {
		//Chargement asynch d'itemCreator
		const {SearchItems} = await import("/@back@/wsp/dialogs/searchItems.js");
		const {TaskCritForMe} = await import("/@back@/wsp/widgets/search/taskCrit.js");
		const {ItemCritTitleRegExp} = await import("/@back@/wsp/widgets/search/itemCrit.js");
		const {UiCritBool} = await import("/@back@/wsp/widgets/search/uiCrit.js");
		const {TplRequestsMenu} = await import("/@back@/wsp/actions/searchesActions.js");

		if (this.kind === 'item')
			ctx.reg.addToList("actions:searchItems:bar:item", "tplRequestsMenu", 1, TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:item", "requests:item:searchItems:item"));
		else
			ctx.reg.addToList("actions:searchItems:bar:task", "tplRequestsMenu", 1, TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:task", "requests:item:searchItems:task"));

		const sd = (ctx as IShortDescCtx).shortDescs;
		const ct = new SearchItems().initialize({
			reg: ctx.reg,
			showMainView: true,
			fromSrcUri: sd && sd[0] && sd[0].srcSt > 0 ? sd[0].srcUri : null,
			fullTextByDefault: true,
			criterions: ctx.reg.getListAsMap(this.kind === 'item' ? "wsp.crit.items" : "wsp.crit.tasks"),
			buildCrit: this.kind === 'task' ? (searchItems) => {
				searchItems.critRoot.appendChild(<h3>Portée</h3>);
				searchItems.critRoot.appendChild(TaskCritForMe.AREA.buildBody(searchItems));
				searchItems.critRoot.appendChild(<h3>Critères</h3>);
				const and = searchItems.critRoot.appendChild(UiCritBool.AREA_AND_ROOT.buildBody(searchItems)) as UiCritBool;
				and.insertCrit(ItemCritTitleRegExp.AREA.buildBody(searchItems));
			} : undefined,
			srcGrid: {
				defaultAction: new SearchItemsSelItem(ctx),
				defaultActionOn: "dblClick",
				actions: this.kind === 'item' ?
					ctx.reg.mergeLists("actions:searchItem:shortDesc:item", "actions:wsp:shortDesc")
					: ctx.reg.mergeLists("actions:searchItem:shortDesc:task", "actions:wsp:shortDesc:task"),
				accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.kind === 'item' ?
					ctx.reg.mergeListsAsMap("accelkeys:searchItem:shortDesc", "accelkeys:wsp:shortDesc:item")
					: ctx.reg.mergeListsAsMap("accelkeys:searchItem:shortDesc:task", "accelkeys:wsp:shortDesc:task"))
			},
			appHeader: {
				mainActions: ctx.reg.mergeLists(SearchItems.APPBAR_MAIN_ACTIONS, this.kind === 'item' ? "actions:searchItems:bar:item" : "actions:searchItems:bar:task"),
			} as OAppHeaderInit<any>,
			searchStore: ctx.reg.getSvc(this.kind === 'item' ? 'searchStore.item' : 'searchStore.task') as ISearchStore,
			msgs: this.kind === 'item' ? undefined : {
				resultsMore: max => `Plus de ${max} tâches trouvées`,
				resultsEmpty: 'Aucune tâche trouvée',
				resultsOne: '1 tâche trouvée',
				resultsCount: count => `${count} tâches trouvées`,
			},
		} as OSearchItemsInit);
		await POPUP.showDialog(ct, ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: this.kind === 'item' ? "Recherche d'items" : "Recherche de tâches"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "90vw",
			initHeight: "90vh"
		}).onNextClose();
	}
}

class SearchItemsSelItem extends SrcAction<ISrcGridCtx> {

	constructor(public parentCtx: IRegPointer<IWspUiEnv>) {
		super("selItem");
		this.exactOne = true;
	}

	execute(ctx: ISrcGridCtx, ev?: Event) {
		const sd = ctx.shortDescs[0];
		const infoBroker = SrcAction.getInfoBroker(this.parentCtx);
		if (sd && infoBroker) {
			POPUP.findPopupableParent(ctx.me).close();
			infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(sd), sd), this.parentCtx.reg.env.uiRoot);
		}
	}
}


/**
 *
 */
export class ShowNetItems extends SrcAction<IShortDescCtx> {

	workbenchView = "netItems";

	constructor() {
		super("showNetItems");
		this._label = "Afficher le réseau d'items...";
		this._icon = "/@skin@/wsp/views/netItems/netItems.svg";
		this._group = 'tools';
		this.exactOne = true;
		this.noFamilies = [EItemTypeFamily.task];
	}

	isVisible(ctx: IShortDescCtx): boolean {
		const workbench = this.findWorkbench(ctx.emitter);
		if (!workbench) return false;
		if (!workbench.getTab(this.workbenchView)) return false;
		return super.isVisible(ctx);
	}

	findWorkbench(from: Node) {
		return DOMSH.findFlatParentElt(from, null, (n): n is Workbench<any> => n instanceof Workbench);
	}

	async execute(ctx: IShortDescCtx, ev?: Event) {
		//Chargement asynch d'itemCreator
		const workbench = this.findWorkbench(ctx.emitter);
		if (!workbench) return;
		const view = await workbench.showView(this.workbenchView) as NetItems;
		if (!view) return;
		const sd = (ctx as IShortDescCtx).shortDescs;
		if (sd && sd[0]) view.setRootSrcRef(ITEM.srcRefSub(sd[0]));
	}
}

/**
 *
 */
export class ReloadSrc extends SrcAction<IShortDescCtx> {

	constructor() {
		super('reloadSrc');
		this._label = "Recharger";
		this._group = "other";
		this.exactOne = true;
	}

	isVisible(ctx: IShortDescCtx): boolean {
		if (ITEM.getSrcUriType(this.getFirstSrcUri(ctx) || '') !== "item") return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IShortDescCtx, ev?: Event) {
		const wsp = ctx.reg.env.wsp;
		await wsp.wspServer.wspsLive.reloadHouse(WSP.buildWspRef(wsp.code, ctx.shortDescs[0]));
		const info = new InfoRefreshItemDisplays(SRC.srcRef(ctx.shortDescs[0]));
		const infoBroker = SrcAction.getInfoBroker(ctx);
		infoBroker?.dispatchInfo(info, this);
	}
}


/**
 * Création d'un item.
 */
export class CreateItem<C extends IShortDescCtx> extends SrcAction<C> {

	constructor() {
		super('createItem');
		this._label = "Créer un item...";
		this._group = "create";
		this.zeroOrOne = true;
		// Perm évaluée dans le contexte src courant. Acceptable si conf ItemCreator sans changement d'espace (spaceUi:"readOnly)
		this._enableSrcPerms = ["action.src#create.item"];
	}

	async execute(ctx: C, ev?: Event): Promise<JSrcFields | null> {
		//Chargement asynch d'itemCreator
		const {ItemCreator} = await import("/@back@/wsp/dialogs/itemCreator.js");
		return ItemCreator.openCreateItem(ItemCreator.setDefaultConfigFromReg(ctx.reg, {
			itemTypesTree: {textFilter: ''},
			spaceUri: ctx.shortDescs.length > 0 ? ITEM.extractSpaceUri(ctx.shortDescs[0].srcUri) : "",
			spaceUi: "readOnly",
		}), "Créer un item...", ctx.emitter, ev).onNextClose();
	}
}

/**
 * Création d'un espace.
 */
export class CreateSpace<C extends IShortDescCtx> extends SrcAction<C> {

	constructor() {
		super('createSpace');
		this._label = "Créer un espace...";
		this._group = "create";
		this.zeroOrOne = true;
		// FIXME : en l'état, cette perm est évaluée sur le noeud courant, et non pas sur l'espace parent (si on est dans le contexte d'un item)
		//		=> potentiellement faux
		//		contournement partiel en ajoutant un contrôle dans le execute
		this._enableSrcPerms = ["action.src#create.space"];
	}

	async execute(ctx: C, ev?: Event): Promise<JSrcFields> {
		const reg: IReg<IWspUiEnv> = REG.getReg(ctx);
		//Chargement asynch
		const {SrcNewCode} = await import("/@back@/wsp/dialogs/srcNewCode.js");

		const parentSrcUri = this.getParentSpace(ctx, ev);

		// Evaluation des perms sur l'espace parent effectif (cf FIXME ci dessus)
		if (ctx.shortDescs && parentSrcUri !== ctx.shortDescs[0]?.srcUri) {
			const parentShortDesc = await WSP.fetchShortDesc(reg.env.wsp, ctx.emitter, parentSrcUri);
			if (!reg.hasPermission(this._enableSrcPerms, parentShortDesc.srcRoles, reg.env.universe.auth.currentUser.isSuperAdmin, parentShortDesc.srcRi)) {
				ERROR.show("Vous ne disposez pas des autorisations nécessaires pour effectuer cette création d'espace.", null, ctx);
				return;
			}
		}
		const name = ITEM.extractSpaceLabel(parentSrcUri);
		const ti = parentSrcUri ? `Créer un espace en fils de "${name}"` : "Créer un espace à la racine de l'atelier...";
		const ct = new SrcNewCode().initialize({
			reg: ctx.reg,
			parentFolder: parentSrcUri,
			targetSrcUriType: "space"
		});
		const newCode = await POPUP.showDialog<string>(ct, ctx.emitter, {titleBar: {barLabel: {label: ti}}}).onNextClose();
		return newCode ? WSP.createSpaceOrFolder(ctx.reg.env.wsp, null, ctx.emitter, SRC.addLeafToUri(parentSrcUri, newCode)) : null;
	}

	protected getParentSpace(ctx: C, ev?: Event) {
		return ctx.shortDescs.length > 0 ? ITEM.extractSpaceUri(ctx.shortDescs[0].srcUri) : "";
	}
}

/**
 * Suppression d'une source.
 */
export class DeleteSrc extends SrcAction<IShortDescCtx> {

	constructor() {
		super('delSrc');
		this._label = "Supprimer";
		this._group = "edit";
		this.atLeastOne = true;
		this._enableSrcPerms = ["action.src#delete.node"];
	}

	async execute(ctx: IShortDescCtx, ev?: Event) {
		let ok: boolean;
		if (ctx.shortDescs.length === 1) {
			const uri = ctx.shortDescs[0].srcUri;
			const leafName = SRC.extractLeafFromUri(uri);
			switch (ITEM.getSrcUriType(uri)) {
			case 'item':
				ok = await POPUP.confirm(`Supprimer cet item "${leafName}" de l'atelier ?`, ctx.emitter);
				break;
			case 'space':
				ok = await POPUP.confirm(`Supprimer cet espace "${leafName}" de l'atelier ?`, ctx.emitter);
				break;
			case 'res':
				const itemName = ITEM.extractItemCode(uri);
				ok = await POPUP.confirm(`Supprimer cette ressource "${leafName}" de l'item "${itemName}" ?`, ctx.emitter);
				break;
			}
		} else {
			const length = ctx.shortDescs.length;
			ok = await POPUP.confirm(`Supprimer ces ${length} éléments de l'atelier ?`, ctx.emitter);
		}
		if (ok) {
			try {
				await WSP.srcDelete(ctx.reg.env.wsp, ctx.emitter, ctx.shortDescs.map(SRC.srcRef));
			} catch (e) {
				ERROR.report("Une erreur est survenue lors de la suppression.", e, ctx);
			}
		}
	}
}

REG.reg.registerSvc('src/delete', 1, new DeleteSrc());


/** Flag indiquant qu'on est sur une action de déplacement d'une source (rename, move...) */
export interface IMoveAction {
	isMove?: boolean
}

export class MoveToSrc<C extends IShortDescCtx> extends SrcAction<C> implements IMoveAction {

	/** Filtre indiquant les noeuds susceptibles d'être des cible du déplacement acceptables en fonction des sources. */
	static makeTargetFilter(srcs: JSrcFields[]): (srcUri: srcUri) => boolean {
		//dossiers des fichiers sources sélectionnés
		const folderEntries = new Set<srcUri>();
		//sources sélectionnés de type dossier
		const folders = [] as srcUri[];
		for (const src of srcs) {
			folderEntries.add(SRC.extractUriParent(src.srcUri));
			if (src.srcSt === ESrcSt.folder) folders.push(src.srcUri);
		}
		return (srcUri: srcUri): boolean => {
			//on ne peut sélectionner le dossier d'un des fichiers sélectionnés
			if (folderEntries.has(srcUri)) return false;
			//ni toute la descendance des dossiers sélectionnés.
			for (const folder of folders) if (SRC.isSubUriOrEqual(folder, srcUri)) return false;
			return true;
		};
	}

	targetFolder?: srcUri | JSrcFields;
	targetFolderUi?: 'choose' | 'hide';

	//TODO airItem?: 'never' | 'forced' | 'prefered' | 'allowed';

	_titlePopup = "Déplacer vers...";
	_moveButtonLabel = "Déplacer";

	constructor(id?: string) {
		// FIXME : contrôle perms sur la dst
		// @see eu.scenari.wsp.service.wspsrc.SvcWspSrcDialog.checkPermForCopy
		// @see  eu.scenari.wsp.service.wspsrc.SvcWspSrcDialog.checkPermForMove
		super(id || 'moveSrc');
		this._label = "Déplacer...";
		this._group = "edit";
		this.atLeastOne = true;
		// Contrôle de perm sur la dst dans `isEnabled` quand l'information de target est disponible,
		// ou dans execute quand elle n'est pas disponible
		this._enableSrcPerms = ["write.node.moveFrom"];
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		if (ctx.reg.getPref("wsp.hideWspStruct")) return false;
		return true;
	}

	isEnabled(ctx: C): boolean {
		// Si la cible est connue, on peut vérifier dessus la perm
		if (!this.canChooseTarget && typeof this.targetFolder === 'object')
			if (!REG.getReg(ctx).hasPermission("action.src#moveTo", this.targetFolder.srcRoles, null, this.targetFolder.srcRi)) return false;
		return super.isEnabled(ctx);
	}

	get isMove(): boolean {return true}

	setTarget(space: srcUri | JSrcFields, targetSpaceUi: 'choose' | 'hide', /*airItem?: 'never' | 'forced' | 'prefered' | 'allowed'*/): this {
		this.targetFolder = space;
		this.targetFolderUi = targetSpaceUi || 'choose';
		//this.airItem = airItem || 'never';
		return this;
	}

	get canChooseTarget(): boolean {return this.targetFolderUi == null || this.targetFolderUi === 'choose'}

	/** Retourne les uris déplaceées. */
	async execute(ctx: C, ev?: Event): Promise<srcUri[]> {
		const srcs = ctx.shortDescs.concat(); //raceCond stabilisation de la liste (traitements async).
		const targetUriFilter = MoveToSrc.makeTargetFilter(srcs);

		//0 - save all en //
		const saving = ctx.reg.env.wsp.wspServer.wspsLive.saveAllHouses();

		//1 - espace cible
		let targetFolder = this.targetFolder;

		function targetSrcUri(target: srcUri | JSrcFields) {
			return target != null ? (typeof target === 'object' ? target.srcUri : target) : null;
		}

		if (this.canChooseTarget) {
			//Chargement asynch
			const {SpaceSelector} = await import("/@back@/wsp/dialogs/spaceSelector.js");
			const spaceSelector = new SpaceSelector().initialize({
				reg: ctx.reg,
				startSel: targetSrcUri(this.targetFolder) != null ? targetSrcUri(this.targetFolder) : ITEM.extractSpaceUri(this.getFirstSrcUri(ctx)),
				buttonLabel: this._moveButtonLabel,
				spaceTree: {
					showRoot: true
				},
				onSelChange: (src: JSrcFieldsTree, spSel: SpaceSelector) => {
					if (!src) spSel.setMsg("Sélectionnez l'espace cible du déplacement.", false);
					else if (spSel.evalPerm("action.src#moveTo", src, "Vous ne disposez pas des droits pour déplacer vers cet espace.", "Il est impossible de déplacer un contenu vers cet espace.")) {
						if (targetUriFilter(src.srcUri)) spSel.setMsg(null, true);
						else spSel.setMsg("Cet espace ne peut pas être la cible du déplacement.", false);
					}
				}
			});

			spaceSelector.spaceTree.grid.lineDrawer = {
				redrawLine(row: IGridDataRow, line: HTMLElement): void {
					DOM.setStyle(line, "color", targetUriFilter((row.rowKey as JSrcFieldsTree).srcUri) ? "" : "gray");
				}
			};

			spaceSelector.doSelect = async function (this: SpaceSelector) {
				const spSrc = this.spaceTree.shortDescs[0];
				if (!spSrc) return;
				if (!await checkForConflicts(ctx, srcs, spSrc.srcUri)) return;
				this.constructor.prototype.doSelect.call(this);
			};

			const spSel = await POPUP.showDialog<JSrcFields>(spaceSelector, ctx.emitter, {titleBar: {barLabel: {label: this._titlePopup}}, resizer: {}}).onNextClose();
			targetFolder = spSel ? spSel.srcUri : null;
		} else {
			if (targetFolder == null) return null;
			if (!await checkForConflicts(ctx, srcs, targetSrcUri(targetFolder), 'move')) return null;
		}

		//2 - déplacement
		if (targetFolder != null && targetUriFilter(targetSrcUri(targetFolder))) {
			try {
				await saving;
			} catch (e) {
				ERROR.report("Une erreur est survenue durant l'enregistrement préalable de vos éditions en cours. Le déplacement a été annulé.", e, ctx);
			}
			try {
				return await WSP.srcMoveTo(ctx.reg.env.wsp, ctx.emitter, srcs.map((s) => s.srcUri), targetSrcUri(targetFolder));
			} catch (e) {
				MoveToSrc.notifError(ctx, e);
			}
			return null;
		}
	}

	static async notifError(ctx: IShortDescCtx, e: any) {
		switch (await ERROR.extractDescError(e)) {
		case "sourceNotFound":
			return POPUP.confirm("La source du déplacement n'existe plus.", ctx.emitter, {cancelLbl: null});
		case "sourceUriAlreadyUsedInMasterOrOtherLayer" :
			return POPUP.confirm("Le même chemin source est utilisé par l'atelier principal ou un autre atelier calque, le déplacement est impossible.", ctx.emitter, {cancelLbl: null});
		case "targetUriAlreadyUsedInMasterOrOtherLayer" :
			return POPUP.confirm("Le chemin cible de ce déplacement est utilisé par l'atelier principal ou un autre atelier calque, le déplacement est impossible.", ctx.emitter, {cancelLbl: null});
		case "sourceUsedWhereFolderTargetIsNot" :
			return POPUP.confirm("Une ressource à déplacer est déjà exploitée dans l'atelier principal ou un autre atelier calque. Elle ne peut être déplacée dans un espace local à un calque.", ctx.emitter, {cancelLbl: null});
		case "conflictUri" :
			return POPUP.confirm("Le chemin cible de ce déplacement est en conflit avec un autre contenu car une simple différence de casse (minuscule/majuscule) est interdite, le déplacement est impossible.", ctx.emitter, {cancelLbl: null});
		default:
			if (e.response?.status === 403) {
				return ERROR.show("Vous ne disposez pas des autorisations nécessaires pour effectuer ce déplacement.", null, ctx);
			} else {
				return ERROR.report("Une erreur est survenue lors du déplacement.", e, ctx);
			}
		}
	}

}

/** Demandes de confirmations en cas d'écrasement / fusion de contenu pour les actions de MoveTo / CopyTo. */
async function checkForConflicts(ctx: IShortDescCtx, srcs: JSrcFields[], targetFolder: srcUri, alwaysConfirm?: 'move'): Promise<boolean> {
	const spEntries = await new FolderEntries(ctx.reg.env.wsp, ctx.emitter).setFolderUri(targetFolder);
	const conflicts = srcs.filter((s) => spEntries.getChildStateNow(SRC.extractLeafFromUri(s.srcUri)) === 'used');
	if (conflicts.length > 0) {
		const conflictFiles = conflicts.filter((s) => s.srcSt === ESrcSt.file);
		//FIXME personnaliser message si espaces ou dossier de ressources...
		const itemList = conflictFiles.map((s) => SRC.extractLeafFromUri(s.srcUri)).join(', ');
		const space = ITEM.extractSrcLabel(targetFolder, "[Racine de l'atelier]");
		if (conflictFiles.length > 0 && conflictFiles.length < conflicts.length) {
			return await POPUP.confirm(`Attention, les contenus [${itemList}] dans '${space}' vont être écrasés et les espaces fusionnés.`, ctx.emitter, {okLbl: "Écraser et fusionner", kind: "warning"});
		} else if (conflictFiles.length > 0) {
			return await POPUP.confirm(`Attention, les contenus [${itemList}] dans '${space}' vont être écrasés.`, ctx.emitter, {okLbl: "Écraser", kind: "warning"});
		} else {
			return await POPUP.confirm(`Attention, des espaces dans '${space}' vont être fusionnés (et leurs contenus potentiellement écrasés).`, ctx.emitter, {okLbl: "Fusionner", kind: "warning"});
		}
	} else if (alwaysConfirm === 'move') {
		const space = ITEM.extractSrcLabel(targetFolder, "[Racine de l'atelier]");
		const numItems = srcs.length;
		return await POPUP.confirm(`Confirmez le déplacement de ${numItems} contenu(s) vers '${space}'.`, ctx.emitter, {okLbl: "Déplacer"});
	}
	return true;
}


/**
 * Copie un ensemble de sources vers une cible donnée *du même* atelier.
 * Si certaines sources sont déjà dans la cible, elles restent inchangées.
 * Il est impossible de définir une cible dans uri descendante d'une des sources.
 */
export class CopyToSrc<C extends IShortDescCtx> extends SrcAction<C> {

	/** Filtre indiquant les noeuds susceptibles d'être des cibles de la copie acceptables en fonction des sources. */
	static makeTargetFilter(srcs: JSrcFields[]): (srcUri: srcUri) => boolean {
		//sources sélectionnés de type dossier
		const folders = [] as srcUri[];
		for (const src of srcs) {
			if (src.srcSt === ESrcSt.folder) folders.push(src.srcUri);
		}
		return (srcUri: srcUri): boolean => {
			//on ne peut sélectionner toute la descendance des dossiers sélectionnés.
			for (const folder of folders) if (SRC.isSubUriOrEqual(folder, srcUri)) return false;
			return true;
		};
	}

	targetFolder?: srcUri | JSrcFields;
	targetFolderUi?: 'choose' | 'hide';

	//TODO airItem?: 'never' | 'forced' | 'prefered' | 'allowed';

	constructor(id?: string, label?: string, group?: string) {
		super(id || "copyToSrc");
		this._label = label || "Dupliquer vers...";
		this._group = group || "clone";
		this.atLeastOne = true;
		// Contrôle de perm sur la dst dans `isEnabled` quand l'information de target est disponible,
		// ou dans execute quand elle n'est pas disponible
		this._enableSrcPerms = ["read.node.copyFrom"];
	}

	setTarget(space: srcUri | JSrcFields, targetSpaceUi: 'choose' | 'hide', /*airItem?: 'never' | 'forced' | 'prefered' | 'allowed'*/): this {
		this.targetFolder = space;
		this.targetFolderUi = targetSpaceUi || 'choose';
		//this.airItem = airItem || 'never';
		return this;
	}

	get canChooseTarget(): boolean {return this.targetFolderUi == null || this.targetFolderUi === 'choose'}

	isEnabled(ctx: C): boolean {
		// Si la cible est connue, on peut vérifier dessus la perm
		if (!this.canChooseTarget && typeof this.targetFolder === 'object')
			if (!REG.getReg(ctx).hasPermission("action.src#copyTo", this.targetFolder.srcRoles, null, this.targetFolder.srcRi)) return false;
		return super.isEnabled(ctx);
	}


	/** Retourne les uri dupliqués. */
	async execute(ctx: IShortDescCtx, ev?: Event): Promise<srcUri[]> {
		const srcs = ctx.shortDescs.concat(); //raceCond stabilisation de la liste (traitements async).
		//0 - Filtre des cibles possibles
		const targetUriFilter = CopyToSrc.makeTargetFilter(srcs);

		//1 - espace cible
		let targetFolder = this.targetFolder;

		function targetSrcUri(target: srcUri | JSrcFields) {
			return target != null ? (typeof target === 'object' ? target.srcUri : target) : null;
		}

		if (this.canChooseTarget) {
			//Chargement asynch
			const {SpaceSelector} = await import("/@back@/wsp/dialogs/spaceSelector.js");
			const spaceSelector = new SpaceSelector().initialize({
				reg: ctx.reg,
				startSel: targetSrcUri(this.targetFolder) != null ? targetSrcUri(this.targetFolder) : ITEM.extractSpaceUri(this.getFirstSrcUri(ctx)),
				buttonLabel: "Dupliquer vers...",
				spaceTree: {
					showRoot: true
				},
				onSelChange: (src: JSrcFieldsTree, spSel: SpaceSelector) => {
					if (!src) spSel.setMsg("Sélectionnez l'espace cible de la copie.", false);
					else if (spSel.evalPerm("action.src#copyTo", src, "Vous ne disposez pas des droits de copie dans cet espace.", "Impossible de copier dans cet espace.")) {
						if (targetUriFilter(src.srcUri)) spSel.setMsg(null, true);
						else spSel.setMsg("Cet espace, contenu dans une source à copier, ne peut pas être la cible de la copie.", false);
					}
				}
			});

			spaceSelector.spaceTree.grid.lineDrawer = {
				redrawLine(row: IGridDataRow, line: HTMLElement): void {
					DOM.setStyle(line, "color", targetUriFilter((row.rowKey as JSrcFieldsTree).srcUri) ? "" : "gray");
				}
			};

			spaceSelector.doSelect = async function (this: SpaceSelector) {
				const spSrc = this.spaceTree.shortDescs[0];
				if (!spSrc) return;
				if (!await checkForConflicts(ctx, srcs, spSrc.srcUri)) return;
				this.constructor.prototype.doSelect.call(this);
			};

			const spSel = await POPUP.showDialog<JSrcFields | null>(spaceSelector, ctx.emitter, {titleBar: {barLabel: {label: "Dupliquer vers..."}}}).onNextClose();
			targetFolder = spSel ? spSel.srcUri : null;
		} else {
			if (targetFolder == null) return null;
			if (!await checkForConflicts(ctx, srcs, targetSrcUri(targetFolder))) return null;
		}

		//2 - copie
		if (targetFolder != null && targetUriFilter(targetSrcUri(targetFolder))) {
			try {
				return await WSP.srcCopyTo(ctx.reg.env.wsp, ctx.emitter, srcs.map((s) => s.srcUri), targetSrcUri(targetFolder));
			} catch (e) {
				if (e.response?.status === 403) {
					ERROR.show("Vous ne disposez pas des autorisations nécessaires pour effectuer cette copie.", null, ctx);
				} else {
					ERROR.report("Une erreur est survenue lors de la copie.", e, ctx);
				}
				console.error(e);
			}
		}
		return null;
	}

}


/**
 * Import à partir d'une source externe (IShortDescsTransfer.isImport === true).
 */
export class ImportItemsToSrc<C extends IShortDescCtx> extends SrcAction<C> {

	transfer: IShortDescsTransfer;

	targetFolder?: srcUri;
	targetFolderUi?: 'choose' | 'hide';

	//TODO airItem?: 'never' | 'forced' | 'prefered' | 'allowed';

	constructor(transfer: IShortDescsTransfer, id?: string, label?: string, group?: string) {
		super(id || "importToSrc");
		if (!transfer.isImport) throw Error("ShortDescsTransfer.isImport is false!!");
		this.transfer = transfer;
		this._label = label || "Importer...";
		this._group = group || "clone";
		//this.atLeastOne = true; pas compat avec ShortDescsFromUriList qui ne connait pas encore sa liste de shortDescs sur le dragover
	}

	setTarget(space: srcUri, targetSpaceUi: 'choose' | 'hide', /*airItem?: 'never' | 'forced' | 'prefered' | 'allowed'*/): this {
		this.targetFolder = space;
		this.targetFolderUi = targetSpaceUi || 'choose';
		//this.airItem = airItem || 'never';
		return this;
	}

	/** Retourne les uri dupliqués. */
	async execute(ctx: IShortDescCtx, ev?: Event): Promise<JSrcFields[]> {
		//1 - choix espace cible
		let targetFolder = this.targetFolder;
		if (this.targetFolderUi == null || this.targetFolderUi === 'choose') {
			//Chargement asynch
			const {SpaceSelector} = await import("/@back@/wsp/dialogs/spaceSelector.js");
			const spaceSelector = new SpaceSelector().initialize({
				reg: ctx.reg,
				startSel: this.targetFolder != null ? this.targetFolder : ITEM.extractSpaceUri(this.getFirstSrcUri(ctx)),
				buttonLabel: "Importer",
				spaceTree: {
					showRoot: true
				},
				onSelChange: (src: JSrcFieldsTree, spSel: SpaceSelector) => {
					if (!src) spSel.setMsg("Sélectionnez l'espace cible de la copie", false);
					else if (spSel.evalPerm("action.src#importTo", src, "Vous ne disposez pas des droits pour importer dans cet espace", "Impossible d'importer dans cet espace")) {
						spSel.setMsg(null, true);
					}
				}
			});

			const spSel = await POPUP.showDialog<JSrcFields>(spaceSelector, ctx.emitter, {titleBar: {barLabel: {label: "Importer vers..."}}}).onNextClose();
			targetFolder = spSel ? spSel.srcUri : null;
		}

		//2 - import
		if (targetFolder != null) {
			try {
				const imports = [] as JSrcFields[];
				const fromUidSet = new Set();
				this.transfer.shortDescs.forEach((entry) => fromUidSet.add(entry.fromUid || entry));
				const options = {
					targetSrcType: ITEM.getSrcUriType(targetFolder) === 'space' ? 'itemOrSpace' : 'res',
					defaultUriParent: targetFolder,
					allowReplace: true,
					repeatedImports: fromUidSet.size > 1 ? {} : undefined
				} as OImportSrcOptions;
				for (const datas of this.transfer.shortDescs) {
					if (datas.createSrc) {
						const src = await datas.createSrc(options);
						if (src) imports.push(src);
					}
				}
				return imports.length > 0 ? imports : null;
			} catch (e) {
				await ERROR.report("Une erreur est survenue lors de l'import.", e, ctx);
			}
		}
		return null;
	}

}


/**
 * Renomme une source sans déplacement.
 */

export class RenameSrc<C extends IShortDescCtx> extends SrcAction<C> implements IMoveAction {

	constructor() {
		super('renameSrc');
		this._label = "Renommer...";
		this._group = "edit";
		this.atLeastOne = true;
		// Perm "enabled" dans {@link RenameSrc.isEnabled} car dépendante de la nature de la src (space, item)
	}

	isEnabled(ctx: IShortDescCtx): boolean {
		const reg = REG.getReg(ctx);
		for (const sd of ctx.shortDescs)
			if (!reg.hasPermission(ITEM.getSrcUriType(sd.srcUri) === "space" ? "rename.space" : "rename.item", sd.srcRoles, null, sd.srcRi)) return false;
		//Pas de renommage possible si on pointe au moins un item externe
		return super.isEnabled(ctx as C) && ctx.shortDescs.findIndex((src) => ITEM.isExtItem(src.srcUri)) < 0;
	}

	get isMove(): boolean {return true}

	async execute(ctx: IShortDescCtx, ev?: Event): Promise<srcUri[]> {
		const srcs = ctx.shortDescs.concat(); //raceCond stabilisation de la liste (traitements async).
		const results: srcUri[] = [];
		//0 - save all en //
		const saving = ctx.reg.env.wsp.wspServer.wspsLive.saveAllHouses();
		//Chargement asynch
		const {SrcNewCode} = await import("/@back@/wsp/dialogs/srcNewCode.js");
		for (let i = 0; i < srcs.length; i++) {
			const src = srcs[i];
			const ct = new SrcNewCode().initialize({
				reg: ctx.reg,
				parentFolder: ITEM.isAirItem(src.srcUri) ? null : SRC.extractUriParent(src.srcUri),
				currentCode: SRC.extractLeafFromUri(src.srcUri),
				targetSrcUriType: ITEM.getSrcUriType(src.srcUri),
				doBtnLabel: "Renommer"
			});
			const newCode = await POPUP.showDialog<string>(ct, ctx.emitter, {titleBar: {barLabel: {label: "Renommer..."}}}).onNextClose();
			if (newCode) {
				try {
					await saving;
				} catch (e) {
					await ERROR.report("Une erreur est survenue durant l'enregistrement préalable de vos modifications en cours. Le renommage a été annulé.", e, ctx);
				}
				try {
					results.push(await WSP.srcRename(ctx.reg.env.wsp, ctx.emitter, src, newCode));
				} catch (e) {
					RenameSrc.notifError(ctx, e);
				}
			} else if (i < srcs.length - 1) {
				const count = srcs.length - i - 1;
				if (!await POPUP.confirm(`Il reste ${count} à renommer dans votre sélection initiale.`, ctx.emitter, {okLbl: "Poursuivre le renommage", cancelLbl: "Abandonner"})) break;
			}
		}
		return results
	}

	static async notifError(ctx: IShortDescCtx, e: any) {
		switch (await ERROR.extractDescError(e)) {
		case "sourceNotFound":
			return POPUP.confirm("La source du renommage n'existe plus.", ctx.emitter, {cancelLbl: null});
		case "sourceUriAlreadyUsedInMasterOrOtherLayer" :
			return POPUP.confirm("Le même chemin source est utilisé par l'atelier principal ou un autre atelier calque, le renommage est impossible.", ctx.emitter, {cancelLbl: null});
		case "targetUriAlreadyUsedInMasterOrOtherLayer" :
			return POPUP.confirm("Le chemin cible de ce renommage est utilisé par l'atelier principal ou un autre atelier calque, le déplacement est impossible.", ctx.emitter, {cancelLbl: null});
		case "conflictUri" :
			return POPUP.confirm("Un renommage avec un simple changement de casse (minuscule/majuscule) est interdit.", ctx.emitter, {cancelLbl: null});
		default:
			return ERROR.report("Une erreur est survenue lors du renommage.", e, ctx);
		}
	}
}


/**
 * Duplique unitairement chaque ressource **dans son propre espace** (ou en airItem si la source est en airItem).
 * Attention, même en sélection multiple, les actions sont unitaires : 2 items dupliqués qui se référencent mutuellement
 * garderont leur référence vers l'item d'origine et non vers l'autre item dupliqué.
 */
export class DuplicateSrc<C extends IShortDescCtx> extends SrcAction<C> {

	constructor(id?: string) {
		super(id || "duplicateSrc");
		this._group = "clone";
		this.atLeastOne = true;
		this._enableSrcPerms = ["write.node.duplicate"];
	}

	getLabel(ctx: C) {
		if (this._label) return super.getLabel(ctx);
		// Si plusieurs sources : "un à un" pour exprimer l'idée que les refs des items dupliqués continueront à pointer
		// les items sources et non les autres items dupliqués.
		return ctx.shortDescs.length > 1 ? "Dupliquer un à un..." : "Dupliquer...";
	}

	/** Retourne le ou les srcUris dupliqués. */
	async execute(ctx: IShortDescCtx, ev?: Event): Promise<srcUri[]> {
		const srcs = ctx.shortDescs.concat(); //raceCond stabilisation de la liste (traitements async).
		const results: srcUri[] = [];
		//Chargement asynch
		const {SrcNewCode} = await import("/@back@/wsp/dialogs/srcNewCode.js");
		for (let i = 0; i < srcs.length; i++) {
			const src = srcs[i];
			const newCodeUi = new SrcNewCode().initialize({
				reg: ctx.reg,
				parentFolder: ITEM.isAirItem(src.srcUri) ? null : SRC.extractUriParent(src.srcUri),
				currentCode: SRC.extractLeafFromUri(src.srcUri),
				targetSrcUriType: ITEM.getSrcUriType(src.srcUri),
				doBtnLabel: "Dupliquer"
			});
			const newCode = await POPUP.showDialog<string>(newCodeUi, ctx.emitter, {titleBar: {barLabel: {label: "Dupliquer..."}}}).onNextClose();
			if (newCode) {
				try {
					results.push(await WSP.srcDuplicate(ctx.reg.env.wsp, ctx.emitter, src, newCode));
				} catch (e) {
					DuplicateSrc.notifError(ctx, e);
				}
			} else if (i < srcs.length - 1) {
				const count = srcs.length - i - 1;
				if (!await POPUP.confirm(`Il reste ${count} contenus à dupliquer dans votre sélection initiale.`, ctx.emitter, {okLbl: "Poursuivre la duplication", cancelLbl: "Abandonner"})) break;
			}
		}
		return results;
	}

	static async notifError(ctx: IShortDescCtx, e: any) {
		switch (await ERROR.extractDescError(e)) {
		case "sourceNotFound":
			return POPUP.confirm("La source de la duplication n'existe plus.", ctx.emitter, {cancelLbl: null});
		case "targetUriAlreadyUsedInMasterOrOtherLayer" :
			return POPUP.confirm("Le chemin cible de cette duplication est utilisé par l'atelier principal ou un autre atelier calque, la duplication est impossible.", ctx.emitter, {cancelLbl: null});
		case "conflictUri" :
			return POPUP.confirm("Le chemin cible de cette duplication est en conflit avec un autre contenu car une simple différence de casse (minuscule/majuscule) est interdite, la duplication est impossible.", ctx.emitter, {cancelLbl: null});
		default:
			return ERROR.report("Une erreur est survenue lors de la duplication.", e, ctx);
		}
	}
}

/**
 * Importe des fichiers externes dans le contexte shortDesc courant.
 * Impl limitée à l'import d'items, à revoir pour l'import de res dans un item folder.
 */
export class ImportFiles<C extends IShortDescCtx> extends SrcAction<C> {

	constructor(id?: string) {
		super(id || 'importFile');
		this._label = "Importer un fichier...";
		this._icon = "/@skin@/wsp/actions/impFile.svg";
		this._group = "importExport";
		this.zeroOrOne = true;
		this._enableSrcPerms = ["write.node.import"];
	}

	async execute(ctx: C, ev?: Event): Promise<JSrcFields[] | null> {
		//FIXME utiliser https://wicg.github.io/file-system-access/#api-showopenfilepicker (en attente impl par Firefox).
		const input = document.createElement('input');
		input.type = "file";
		input.multiple = true;
		input.click();
		const files = await new Promise<FileList>((resolve) => {
			input.onchange = function (this: HTMLInputElement) {
				resolve(this.files.length > 0 ? this.files : null);
			}
		});
		if (files) {
			const svc = ctx.reg.getSvc<IImportInWspSvc>("wspImportInWsp");
			if (!svc) return null;
			const from = ctx.shortDescs[0];
			const defaultUriParent = from ? ITEM.extractSpaceUri(from.srcUri) : ""; //TODO cas où from est airItem, donc airItem ?

			const options = {
				targetSrcType: "item",
				defaultUriParent: defaultUriParent,
				allowReplace: true,
				repeatedImports: files.length > 1 ? {} : undefined
			} as OImportSrcOptions;

			const shortDescs: JSrcFields[] = [];
			for (let i = 0; i < files.length; i++) {
				const file = files.item(i);
				const shortDesc = await svc.importSrc(file, options, ctx.reg, ctx.emitter);
				if (shortDesc) {
					shortDescs.push(shortDesc);
				} else {
					if (i < files.length - 1 && !await POPUP.confirm("Voulez-vous poursuivre l'import des fichiers suivants ?", ctx.emitter, {okLbl: "Poursuivre"})) return;
				}
			}
			const infoBroker = SrcAction.getInfoBroker(ctx);
			if (infoBroker) {
				if (shortDescs.length === 1)
					infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(shortDescs[0]), shortDescs[0]), ctx.emitter);
				else
					infoBroker.dispatchInfo(new InfoSelectUris(shortDescs.map((entry) => entry.srcUri)), ctx.emitter);
			}
			//const shortDesc = await svc.importItem(file, options, ctx.reg, ctx.emitter);

			//InfoSelectUris
			//if (infoBroker && shortDesc) infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(shortDesc), shortDesc), ctx.emitter);
			return shortDescs;
		}
		return null;
	}
}

/**
 * Importe une archive Scar (ou ScWsp).
 *
 * FIXME notif avancement UPLOAD + cancel...
 */
export class ImportScar<C extends IShortDescCtx> extends SrcAction<C> {

	static async doImportScar(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, content: File, replaceIfExist: boolean): Promise<boolean> {
		const progress = POPUP.showProgress(uiContext, "Import de l'archive en cours...");
		try {
			let resp = await ITEM.importScar(wsp, uiContext, refUri, content, replaceIfExist, progress.progressCallback);
			progress.close();
			if (resp.error) throw resp.error;
			if (resp.status != 200 && resp.status != 204) {
				if (resp.status !== 420) {
					await ERROR.reportError({
						msg: "Une erreur est survenue lors de l'import de l'archive : " + content.webkitRelativePath || content.name,
						details: resp.asText
					});
				} else {
					const name = content.webkitRelativePath || content.name;
					POPUP.showNotifWarning(<div style="padding:1em">
						<p><b>{`L'import de '${name}' a abouti, mais les items suivants ont été rejetés (car non reconnus par ce modèle) :`}</b></p>
						<pre style="max-height:50vh; overflow:auto;">{resp.asText}</pre>
					</div>, uiContext, {autoHide: 0});
				}
			}
			return true;
		} catch (e) {
			progress.close();
			await ERROR.report("Une erreur est survenue lors de l'import de l'archive : " + content.webkitRelativePath || content.name, e);
			return false
		}
	}

	constructor(id?: string) {
		super(id || 'importScar');
		this._label = "Importer une archive...";
		this._icon = "/@skin@/wsp/actions/impArchive.svg";
		this._group = "importExport";
		this.zeroOrOne = true;
		this._enableSrcPerms = ["write.node.import"];
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		const srcUri = this.getFirstSrcUri(ctx);
		return !srcUri || ITEM.getSrcUriType(srcUri) === "space";
	}

	async execute(ctx: C, ev?: Event) {
		const {wsp} = ctx.reg.env;
		let srcUri = this.getFirstSrcUri(ctx) || SRC.URI_ROOT;
		const input = document.createElement('input');
		input.type = "file";
		input.accept = srcUri ? ".scar" : ".scar,.scwsp";
		input.multiple = true;
		input.click();
		const files = await new Promise<FileList>((resolve) => {
			input.onchange = function (this: HTMLInputElement) {
				//console.log("input.onchange:::", this.files);
				resolve(this.files.length > 0 ? this.files : null);
			}
		});
		if (!files) return;
		let replace = false;
		const node = await WSP.fetchSrcTree(wsp, ctx.emitter, srcUri, 1, ["srcNm"]);
		if (node && node.ch && node.ch.length > 0) {
			const space = ITEM.extractSpaceLabel(srcUri);
			const form = <form>
				<p>{space ?
					`L'espace cible '${space}' n'est pas vide, l'import pourrait écraser vos contenus actuels.`
					: `L'atelier n'est pas vide, l'import pourrait écraser vos contenus actuels.`}</p>
				<p><label><input type="radio" name="opt" value="subSp" checked/>Importer dans un nouveau sous-espace...</label></p>
				<p>
					<label>
						<input type="radio" name="opt" value="noReplace"/>Importer dans cet espace sans remplacer les contenus portant le même nom.
						<span> Seuls les nouveaux contenus seront importés.</span>
					</label>
				</p>
				<p>
					<label>
						<input type="radio" name="opt" value="replace"/>Importer dans cet espace en écrasant les contenus existants.
						<span style="color:var(--warning-color)"> Attention, vos contenus portant les mêmes noms seront remplacés.</span>
					</label>
				</p>
			</form> as HTMLFormElement;
			if (!await POPUP.confirm(form, ctx.emitter)) return;
			const choice = (form.elements.namedItem("opt") as any /*RadioNodeList*/).value;
			replace = choice === "replace";
			if (choice === "subSp") {
				//On crée un space...
				let newCode = files[0].name;
				const idx = newCode.indexOf('.');
				if (idx >= 0) newCode = newCode.substring(0, idx);
				const {SrcNewCode} = await import("/@back@/wsp/dialogs/srcNewCode.js");
				const newCodeUi = new SrcNewCode().initialize({
					reg: ctx.reg,
					parentFolder: srcUri,
					defaultCode: newCode,
					targetSrcUriType: "space",
					doBtnLabel: "Importer dans ce nouvel espace"
				});
				newCode = await POPUP.showDialog<string>(newCodeUi, ctx.emitter, {titleBar: {barLabel: {label: "Créer un sous-espace..."}}}).onNextClose();
				if (!newCode) return;
				srcUri = SRC.addLeafToUri(srcUri, newCode);
			}
		}

		for (let i = 0; i < files.length; i++) {
			const file = files.item(i);
			if (!await ImportScar.doImportScar(wsp, ctx.emitter, srcUri, file, replace)) {
				if (i < files.length - 1 && !await POPUP.confirm("Voulez-vous poursuivre l'import des archives suivantes ?", ctx.emitter, {okLbl: "Poursuivre"})) return;
			}
		}
	}
}

/**
 * Modifie le mainstream d'un item à partir d'un Blob.
 * A utiliser lorsque le Blob est connu.
 */
export class WriteMainStreamToSrc<C extends IShortDescCtx> extends SrcAction<C> {

	blob: Blob | Promise<Blob>;

	static findBestType(ctx: IShortDescCtx, cbItems: ClipboardItem[]): string | undefined {
		if (!cbItems || cbItems.length !== 1 || ctx.shortDescs.length !== 1) return undefined;
		const itemType = ctx.reg.env.wsp.wspMetaUi.getItemType(ctx.shortDescs[0].itModel);
		let bestType: string;
		let score = 0;
		for (let t of cbItems[0].types) {
			let s = itemType.matchType(t);
			if (s > score) {
				score = s;
				bestType = t;
			}
		}
		return bestType;
	}

	constructor(blob: Blob | Promise<Blob>, id?: string, label?: string, group?: string) {
		super(id || "writeStreamToSrc");
		this.blob = blob;
		this._label = label || "Coller le contenu";
		this._group = group || "clipboard";
		this.exactOne = true;
	}

	async execute(ctx: C, ev?: Event) {
		if (this.blob instanceof Promise) this.blob = await this.blob;
		if (!this.blob) return;
		const itemLabel = ITEM.extractSrcLabel(ctx.shortDescs[0].srcUri, "");
		if (await POPUP.confirm(<div><p>{"Remplacer le contenu de cet item par celui du presse-papier ?"}</p><p>{itemLabel}</p></div>, ctx.emitter, {okLbl: "Remplacer"})) {
			const sd = ctx.shortDescs[0];
			const infoBroker = SrcAction.getInfoBroker(ctx);
			const progress = POPUP.showProgress(ctx.emitter, "Remplacement en cours...");
			try {
				const shortDesc = await ITEM.update(ctx.reg.env.wsp, ctx.emitter, SRC.srcRef(sd), this.blob, progress.progressCallback, ctx.reg.env.wsp.getShortDescDef());
				progress.close();
				if (infoBroker && shortDesc) infoBroker.dispatchInfo(new InfoFocusItem(SRC.srcRef(shortDesc), shortDesc), null); //utile si action lancée hors de la mainView
			} catch (e) {
				progress.close();
				ERROR.report("Échec au remplacement du contenu. Recommencez ultérieurement.", e);
			}
		}
	}
}

/**
 * Action de coller dans le mainStream à partir du clipboard utilisable par racourci clavier ou en menu contextuel,
 * MAIS PAS en toolbar : pb eval async.
 */
export class PasteContent<C extends IShortDescCtx> extends WriteMainStreamToSrc<C> {

	constructor(id?: string, label?: string, group?: string) {
		super(null, id, label, group);
	}

	isVisible(ctx: C): boolean {
		if (!navigator.clipboard || !navigator.clipboard.read) return false;
		return super.isVisible(ctx);
	}

	/** On bloque dans les barres d'outils (pb clipboard asynchrone). */
	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {return uiContext === 'bar' ? null : undefined}

	async initButtonNode(buttonNode: Element, ctx: C) {
		if (buttonNode instanceof ActionBtn) {
			try {
				const cbItems = await navigator.clipboard.read();
				buttonNode.disabled = WriteMainStreamToSrc.findBestType(ctx, cbItems) == null;
			} catch (e) {
				buttonNode.disabled = true;
				if ((await navigator.permissions.query({name: 'clipboard-read' as any /*bug TS3.5*/})).state === 'denied') {
					POPUP.showNotifWarning("L'accès au presse-papier n'est pas autorisé par votre navigateur. Modifiez les paramètres du site pour rendre opérationnelles les fonctions de copier/coller.", buttonNode);
				}
			}
		}
	}

	async execute(ctx: C, ev?: Event): Promise<void> {
		try {
			const cbItems = await navigator.clipboard.read();
			const bestType = WriteMainStreamToSrc.findBestType(ctx, cbItems);
			if (bestType) {
				this.blob = cbItems[0].getType(bestType);
				return super.execute(ctx, ev);
			}
		} finally {
			this.blob = null;
		}
	}

}

/**
 * Export une archive Scar.
 */
export class ExportScar<C extends IShortDescCtx> extends SrcAction<C> {

	constructor(id?: string) {
		super(id || 'exportScar');
		this._label = "Exporter une archive...";
		this._icon = "/@skin@/wsp/actions/expArchive.svg";
		this._group = "importExport";
		this.atLeastOne = true;
		this._enableSrcPerms = ["read.node.export"];
	}

	async execute(ctx: C, ev?: Event) {
		const popup = new PopupConfirm().initialize({titleBar: {barLabel: {label: "Exporter une archive..."}}, okLbl: "Exporter"});
		const includeNet = <input type="checkbox" checked=""/> as HTMLInputElement;
		const keepSpaces = <input type="checkbox"/> as HTMLInputElement;
		const isSingleItem = ctx.shortDescs.length === 1 && ITEM.getSrcUriType(this.getFirstSrcUri(ctx)) === "item";

		popup.setContent(<section>
			<div><label>{includeNet}<span>{isSingleItem ? "Inclure le réseau descendant complet de cet item" : "Inclure le réseau descendant complet des items sélectionnés"}</span></label></div>
			<div><label>{keepSpaces}<span>Préserver les espaces de l'atelier</span></label></div>
		</section>);

		popup.show({viewPortX: 'middle', viewPortY: 'middle'}, ctx.emitter);

		if (await popup.onNextClose()) {
			const wsp = ctx.reg.env.wsp;
			const scope = includeNet.checked ? "net" : "node";
			const mode = keepSpaces.checked ? "wspTree" : scope == "net" ? "rootAndRes" : "flat";

			let name = wsp.wspTitle;
			if (ctx.shortDescs.length === 1) {
				let leaf = SRC.extractLeafFromUri(this.getFirstSrcUri(ctx));
				if (leaf) {
					const idx = leaf.indexOf(".");
					if (idx >= 0) leaf = leaf.substring(0, idx);
					name = leaf;
				}
			}
			name = IO.getValidFileName(name, ".scar", "dateTime");

			const url = ctx.reg.env.universe.config.wsps.exportUrl.resolve(IO.qs("cdaction", "Export", "param", wsp.code, "format", "jar", "scope", scope, "mode", mode, "link", "relative", "downloadFileName", name)).url;
			const form = <form hidden accept="application/jar"><input type="hidden" name="refUris" value={ctx.shortDescs.map((s) => SRC.srcRef(s)).join('\t')}/></form> as HTMLFormElement;
			form.method = "POST";
			form.action = url;
			document.body.append(form);
			form.submit();
			form.remove();
		}
	}
}

/**
 * Export .scWsp dans l'explorateur si pas de src sélectionnée
 */
export class ExportScwsp<C extends IShortDescCtx> extends ExportWspAction<C> {
	isVisible(ctx: C): boolean {
		if (ctx.shortDescs.length > 0) return false;
		//aucune source sélectionnée
		return super.isVisible(ctx);
	}
}

/**
 * Permissions affectées sur un espace
 */
export class SetPermsSpace<C extends ISetPermsSpaceCtx> extends SrcAction<C> {

	constructor(id?: string) {
		super(id || "openPermsWsp");
		this._label = "Permissions sur l'espace...";
		this._description = "Définir les permissions de cet espace";
		this._group = "admin";
		this._icon = "/@skin@/wsp/actions/spacePerms.svg";
		this.exactOne = this.isBackendDb = true;
		this.requireSrcEnabledPerm(["action.space#set.perms", "admin.node.configRoles.byAdmin"]);
	}

	isVisible(ctx: C): boolean {
		if (ITEM.getSrcUriType(this.getFirstSrcUri(ctx) || '') !== "space") return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event): Promise<void> {
		// Implémentation pour le mode exactOne uniquement
		const reg = ctx.reg;
		const {ObjRolesMapEdit} = await import("/@back@/core/dialogs/objRolesMap.js");
		const item = ctx.shortDescs[0];//mode exactOne
		const name = ITEM.extractSpaceLabel(item.srcUri);
		const itemType = reg.env.wsp.wspMetaUi.getItemType(item.itModel);
		const itemReg = ctx.reg = REG.createSubReg(reg, itemType);
		itemReg.env.securityCtx = SEC.createSub(reg.env.securityCtx, item.srcRoles, item.srcRi);
		await POPUP.showDialog(new ObjRolesMapEdit<IWspActionCtx, IWspUiEnv>().initialize({
			reg: itemReg,
			uiContext: "popup",
			rolesUiLayerSng: "space",
			preSelectAccounts: ctx.preSelectAccounts,
			objThis: ctx,
			objEditRolesMap: (objThis, rolesMap) => {return WSP.srcSetRoles(ctx.reg.env.wsp, ctx.reg.env.uiRoot, item.srcUri, rolesMap)},
			objEditRolesMapPerms: ["action.space#set.perms", "admin.node.configRoles.byAdmin"],
			objFetchRolesMap: (objThis) => {return WSP.srcGetRoles(ctx.reg.env.wsp, ctx.reg.env.uiRoot, item.srcUri)},
			objFetchRolesMapForAccounts: (objThis, pAccounts: string[]) => {return WSP.srcGetRolesForAccounts(ctx.reg.env.wsp, ctx.reg.env.uiRoot, item.srcUri, pAccounts)},
		} as OObjRolesMapInit<IWspActionCtx, IWspUiEnv>), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: name ? `Permissions définies sur l'espace '${name}'` : `Permissions définies sur l'espace`}, closeButton: {}},
			resizer: {},
			initWidth: "40em",
			initHeight: "40em"
		}).onNextClose();
	}
}

export interface ISetPermsSpaceCtx extends IShortDescCtx {
	preSelectAccounts?: string[]
}

/**
 * Affiche la corbeille d'un atelier.
 */
export class OpenWspTrash<C extends IShortDescCtx> extends SrcAction<C> {

	static async openTrashDialog(from: HTMLElement, init: OWspTrashInit) {
		const reg = init.reg;
		const {WspTrash} = await import("/@back@/wsp/dialogs/wspTrash.js");
		const wspTitle = reg.env.wsp.wspTitle;
		await POPUP.showDialog(new WspTrash().initialize(init), from, {
			titleBar: {barLabel: {label: `Corbeille de l'atelier : ${wspTitle}`}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "90vw",
			initHeight: "90vh"
		}).onNextClose();
	}

	constructor(id?: string) {
		super(id || 'showWspTrash');
		this._label = "Corbeille...";
		this._group = "wsp";
		this._enableSrcPerms = ["read.trash"];
		this.isBackendDb = true;
	}

	async execute(ctx: C, ev?: Event) {
		return OpenWspTrash.openTrashDialog(ctx.emitter, {
			reg: ctx.reg,
			showMainView: true
		});
	}
}

export class WspImportSrc implements IImportInWspSvc {

	static async pushResFolder(folder: IFolder, parentUri: srcUri, wsp: Wsp, uiCtx: HTMLElement) {
		if (SRC.isValidPartUri(folder.name) !== true) return; //On saute les noms de dossier non autorisés.
		const newParent = SRC.addLeafToUri(parentUri, folder.name);
		let empty = true;
		for (let entry of folder.entries) {
			if (entry instanceof File) {
				if (SRC.isValidPartUri(entry.name) === true) {
					empty = false;
					await ITEM.update(wsp, uiCtx, SRC.addLeafToUri(newParent, entry.name), entry);
				}
			} else {
				empty = false;
				await WspImportSrc.pushResFolder(entry, newParent, wsp, uiCtx);
			}
		}
		if (empty) {
			//Aucun fils créé, on ajoute un dossier vide.
			await ITEM.update(wsp, uiCtx, newParent, null);
		}
	}

	importSrc(body: File | Blob | IFolder, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		switch (options.targetSrcType) {
		case "item":
			return this.importItem(body, options, reg, uiCtx);
		case "space":
			if (isFolder(body)) return this.importSpace(body, options, reg, uiCtx);
			console.trace("importSrc with targetSrcType='space' but body not a IFolder");
			return null;
		case "itemOrSpace":
			if (isFolder(body) && ITEM.isValidCodeItem(body.name) !== true) return this.importSpace(body, options, reg, uiCtx);
			return this.importItem(body, options, reg, uiCtx);
		case "res":
			return this.importRes(body, options, reg, uiCtx);
		}
		console.trace("importSrc with options.targetSrcType undefined", options);
		return null;
	}

	async importItem(body: File | Blob | IFolder, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		if (options.allowReplace) {
			const item: JSrcFields | undefined = (reg.env as IItemUiEnv).longDesc;
			const parentUri = options.defaultUriParent != null ? options.defaultUriParent : item ? ITEM.extractSpaceUri(item.srcUri) : "";
			return this.doImportAllowReplace('item', body, parentUri, options, reg, uiCtx);
		} else {
			//Création uniquement...
			const {ItemCreator} = await import("/@back@/wsp/dialogs/itemCreator.js");
			const item = (reg.env as IItemUiEnv).longDesc;
			const mimeType = 'type' in body ? body.type : null;
			const name = 'name' in body ? body.name : null;
			return ItemCreator.openCreateItem(ItemCreator.setDefaultConfigFromReg(reg, {
				body,
				itemTypesTree: {
					itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
						// On ne garde que les n ItemType de matchType le plus élevé
						// Cas particulier : les it de familly XML pour lesquels on ne garde qu'un it de façon arbitraire, les modalités
						// de filtre étant trop pauvres ici (pas d'accès au contenu de l'item) pour sélectionner le bon it
						// (nécessiterai de rentrer dans l'item, et de matcher le tag racine)
						// matchScore mini : 2
						const minMatchScore = 2;
						if (options.sgnPattern && !options.sgnPattern.test(cur.getSgn())) return acc;
						let prevIt = acc[acc.length - 1];
						let prevMatchScore = prevIt ? prevIt.matchType(mimeType, name) : minMatchScore;
						let currMatchScore = cur.matchType(mimeType, name);
						if (currMatchScore > prevMatchScore) {
							// On clean le tableau pour garder cette nouvelle valeur
							while (acc.length) acc.pop();
							acc.push(cur);
						} else if (currMatchScore === prevMatchScore && (!prevIt || prevIt.getFamily() !== EItemTypeFamily.xml)) {
							acc.push(cur);
						}
						return acc;
					}
				},
				spaceUri: options.defaultUriParent != null ? options.defaultUriParent : item ? ITEM.extractSpaceUri(item.srcUri) : "",
				codeItem: body instanceof File ? body.name : null
			}), "Importer un item...", uiCtx).onNextClose();
		}
	}

	async importRes(body: File | Blob | IFolder, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		if (!options.allowReplace) console.log(":::TODO WspImportSrc.importRes() with options.allowReplace=false", body);
		const parentUri = options.defaultUriParent != null ? options.defaultUriParent : (reg.env as IItemUiEnv).longDesc?.srcUri || "";
		return this.doImportAllowReplace('res', body, parentUri, options, reg, uiCtx);
	}

	async importSpace(folder: IFolder, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		if (!options.allowReplace) console.log(":::TODO WspImportSrc.importRes() with options.allowReplace=false", folder);
		let parentUri = options.defaultUriParent;
		if (parentUri == null) {
			const item: JSrcFields | undefined = (reg.env as IItemUiEnv).longDesc;
			parentUri = item ? ITEM.extractSpaceUri(item.srcUri) : "";
		}
		return this.doImportAllowReplace('space', folder, parentUri, options, reg, uiCtx);
	}

	protected async doImportAllowReplace(type: 'item' | 'space' | 'res', body: File | Blob | IFolder, parentUri: srcUri, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		//Check du nom...
		const wsp = reg.env.wsp;
		let name = 'name' in body ? body.name : null;
		if (ITEM.isValidCode(type, name) !== true) {
			name = await this.buildName(type, reg, uiCtx, parentUri, name);
		} else if (!options.repeatedImports?.replaceAll) {
			const src = await WSP.fetchShortDesc(wsp, uiCtx, SRC.addLeafToUri(parentUri, name));
			if (src.srcSt !== ESrcSt.none) {
				if (options.repeatedImports?.replaceNothing) return;
				name = await this.buildName(type, reg, uiCtx, parentUri, name, options.repeatedImports);
			}
		}
		if (!name) return;
		const progress = POPUP.showProgress(uiCtx, "Import en cours...");
		try {
			if (body instanceof Blob) {
				return await ITEM.update(wsp, uiCtx, SRC.addLeafToUri(parentUri, name), body, progress.progressCallback, wsp.getShortDescDef());
			} else {
				//IFolder
				if (type === "item" && body.name !== name) {
					//La source est un dossier, la cible est un item, et le dossier a été renommé.
					if (wsp.wspMetaUi.getItemTypes().find((it: ItemType) => it.matchType(null, name) > 1 && it.getFamily() === EItemTypeFamily.res)) {
						//L'item cible est une res, on tente le renommage aussi du mainStream de l'itemRes.
						for (let i = 0; i < body.entries.length; i++) {
							const entry = body.entries[i];
							if (entry.name === body.name && entry instanceof File) {
								body.entries[i] = new File([entry], name);
								break;
							}
						}
					}
				}
				body.name = name;
				await WspImportSrc.pushResFolder(body, parentUri, wsp, uiCtx);
				return await wsp.fetchShortDesc(SRC.addLeafToUri(parentUri, name));
			}
		} catch (e) {
			progress.close();
			POPUP.showNotifError("Une erreur est survenue durant l'envoi du contenu.", uiCtx);
			return null;
		} finally {
			progress.close();
		}
	}

	protected async buildName(type: 'item' | 'space' | 'res', reg: IReg<IWspUiEnv>, uiCtx: HTMLElement, parentUri: string | null, currentCode: string, freeStates?: OImportRepeatStates) {
		const ct = new SrcNewCode().initialize({
			reg: reg,
			parentFolder: parentUri,
			defaultCode: currentCode,
			targetSrcUriType: type,
			allowReplace: true,
			doBtnLabel: (state, canReplace) => (state === 'used' && canReplace) ? "Remplacer" : "Importer",
			cancelBtnLabel: freeStates ? (state, canReplace, doAllChecked) => doAllChecked ? "Annuler" : "Ignorer" : "Annuler",
			doAllBtnLabel: freeStates ? "Appliquer pour tout" : undefined,
		});
		const name = await POPUP.showDialog<string | null>(ct, uiCtx, {titleBar: {barLabel: {label: "Importer un item"}}}).onNextClose();
		if (freeStates && ct.config.doAllSelected) {
			if (name) freeStates.replaceAll = true;
			else freeStates.replaceNothing = true;
		}
		return name;
	}
}

/**
 * Svc d'import d'un item à partir d'une source externe (appelé sur session de drag, copier/coller et actions d'import).
 */
REG.reg.registerSvc("wspImportInWsp", 1, new WspImportSrc());


/**
 * Svc d'import d'une source à partir d'un autre wsp du même entrepot.
 * Impl en lazyLoading du Svc.
 */
export class WspImportInterWspLazy implements IImportInterWspSvc {

	impl?: IImportInterWspSvc

	constructor(public lib: string) {}

	async importSrc(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		if (!this.impl) this.impl = await new (await reg.env.resolver.importJs(this.lib) as typeof import("/@back@/wsp/actions/importInterWspSvc.js")).WspImportInterWsp();
		return this.impl.importSrc(wspFrom, srcFrom, options, reg, uiCtx);
	}
}

REG.reg.registerSvc("wspImportInterWsp", 1, new WspImportInterWspLazy(":back:wsp/actions/importInterWspSvc.js"));


/**
 * Import à partir d'une url : tentative de création d'un item Remote.
 */
export class WspImportFromUrlWsp implements IImportFromUrlSvc {

	rejectSgnMatch(patternItemSgn: RegExp, reg: IReg<IWspUiEnv>): boolean {
		return reg.env.wsp.wspMetaUi?.getItemTypes().find((it) => patternItemSgn.test(it.getSgn()) && /\bRemoteBinary\b/.test(it.getSgn())) == null;
	}

	async extractShortDescs(data: DataTransfer, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<IShortDescUrlTransfer[]> {
		//cf https://www.rfc-editor.org/rfc/rfc2483#section-5
		const urls = data.getData(data.types.indexOf("text/x-uri-list") >= 0 ? "text/x-uri-list" : "text/uri-list") // "text/x-uri-list" : contournement bug https://bugs.chromium.org/p/chromium/issues/detail?id=239745
			.split(/\r\n/)
			.filter((u) => u && u.charAt(0) !== '#');
		return (await REMOTE.aliasUrls(urls, reg)).map((u) => new ShortDescToImportFromUrl(u, reg, uiCtx));
	}

	async importSrc(url: string, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null> {
		if (options.targetSrcType === "space" || options.targetSrcType === "res" || options.defaultUriParent == null) return null;
		const path = new URL(url).pathname;
		const nmIdx = path.lastIndexOf("/");
		const {ItemCreator} = await import("/@back@/wsp/dialogs/itemCreator.js");
		return ItemCreator.openCreateItem(ItemCreator.setDefaultConfigFromReg(reg, {
			itemTypesTree: {
				itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
					if (/\bRemoteBinary\b/.test(cur.getSgn()) && (!options.sgnPattern || options.sgnPattern.test(cur.getSgn()))) acc.push(cur);
					return acc;
				}
			},
			spaceUri: options.defaultUriParent,
			spaceUi: options.uriParentFrozen ? "readOnly" : "editable",
			codeItem: nmIdx >= 0 ? path.substring(nmIdx + 1) : null,
			newContentOptions: {remoteUrl: url}
		}), "Créer un item pour référencer une ressource distante...", uiCtx).onNextClose();
	}
}

/** Import à partir d'une Uri. */
class ShortDescToImportFromUrl implements IShortDescUrlTransfer {

	url: string
	remoteResDesc?: JRemoteResDescFromClient | null

	get fromUid(): string {
		return this.url;
	}

	constructor(url: string, public reg: IReg<IWspUiEnv>, public uiCtx: HTMLElement) {
		this.url = url.trim();
	}

	createSrc(options: OImportSrcOptions): Promise<JSrcFields | null> {
		const svc = this.reg.getSvc<IImportFromUrlSvc>("wspImportFromUrl");
		return svc?.importSrc(this.url, options, this.reg, this.uiCtx) || null;
	}

	async getRemoteResDesc(): Promise<JRemoteResDescFromClient | null> {
		if (this.remoteResDesc === undefined) try {
			this.remoteResDesc = await REMOTE.fetchResDesc(this.url, this.reg);
		} catch (e) {
			console.log("Fetch remoteResDesc failed", this.url, e);
			this.remoteResDesc = null;
		}
		return this.remoteResDesc;
	}
}

REG.reg.registerSvc("wspImportFromUrl", 1, new WspImportFromUrlWsp());